import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Sparkles, ChevronDown, ChefHat, ArrowDownToLine, Bed, Bath, Shirt, BedDouble, Sofa, Check, List, Map } from 'lucide-react';
import { MobileOptionsSheet } from './MobileOptionsSheet';
import { interiorMenuData, InteriorMenuItem } from '../../types/interiorConfig';
const rooms = [{
  id: 'kitchen',
  name: 'Kitchen',
  floor: 'Main Floor',
  icon: ChefHat,
  planPosition: {
    top: '35%',
    left: '30%'
  }
}, {
  id: 'basement',
  name: 'Basement',
  floor: 'Basement',
  icon: ArrowDownToLine,
  planPosition: {
    top: '50%',
    left: '50%'
  }
}, {
  id: 'primary-bedroom',
  name: 'Primary Bedroom',
  floor: 'Upper Floor',
  icon: Bed,
  planPosition: {
    top: '25%',
    left: '70%'
  }
}, {
  id: 'ensuite',
  name: 'Ensuite',
  floor: 'Upper Floor',
  icon: Bath,
  planPosition: {
    top: '40%',
    left: '75%'
  }
}, {
  id: 'laundry',
  name: 'Laundry',
  floor: 'Main Floor',
  icon: Shirt,
  planPosition: {
    top: '70%',
    left: '25%'
  }
}, {
  id: 'bedroom-2',
  name: 'Bedroom 2',
  floor: 'Upper Floor',
  icon: BedDouble,
  planPosition: {
    top: '60%',
    left: '65%'
  }
}, {
  id: 'bedroom-3',
  name: 'Bedroom 3',
  floor: 'Upper Floor',
  icon: BedDouble,
  planPosition: {
    top: '75%',
    left: '70%'
  }
}, {
  id: 'living-room',
  name: 'Living Room',
  floor: 'Main Floor',
  icon: Sofa,
  planPosition: {
    top: '55%',
    left: '45%'
  }
}];
const floorPlanImages: Record<string, string> = {
  Basement: 'https://cdn.magicpatterns.com/uploads/uRMUKbZyW7b6N3baMWficb/Zight_2026-04-17_at_6.48.42_AM.jpg',
  'Main Floor': 'https://cdn.magicpatterns.com/uploads/uRMUKbZyW7b6N3baMWficb/Zight_2026-04-17_at_6.48.42_AM.jpg',
  'Upper Floor': 'https://cdn.magicpatterns.com/uploads/uRMUKbZyW7b6N3baMWficb/Zight_2026-04-17_at_6.48.42_AM.jpg'
};
type RoomPickerTab = 'list' | 'plan';
export function MobileInteriorViewer() {
  const [activeRoom, setActiveRoom] = useState('kitchen');
  const [activeItem, setActiveItem] = useState('Countertop');
  const [isAssistantActive, setIsAssistantActive] = useState(false);
  const [sheetState, setSheetState] = useState<'collapsed' | 'peek' | 'expanded'>('peek');
  const [showRoomPicker, setShowRoomPicker] = useState(false);
  const [roomPickerTab, setRoomPickerTab] = useState<RoomPickerTab>('list');
  const [activeFloor, setActiveFloor] = useState<'Basement' | 'Main Floor' | 'Upper Floor'>('Main Floor');
  // Flatten interiorMenuData into sequential steps
  const flatSteps = useMemo(() => {
    const steps: {
      item: InteriorMenuItem;
      category: string;
    }[] = [];
    const traverse = (items: InteriorMenuItem[], parentCategory: string) => {
      items.forEach(item => {
        if (item.type === 'item' && (item.options || !item.children)) {
          steps.push({
            item,
            category: parentCategory
          });
        }
        if (item.children) {
          traverse(item.children, item.type === 'category' ? item.name : parentCategory);
        }
      });
    };
    traverse(interiorMenuData, 'General');
    return steps;
  }, []);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = flatSteps[currentStepIndex];
  const handleNextStep = () => {
    if (currentStepIndex < flatSteps.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      setActiveItem(flatSteps[nextIndex].item.name);
    }
  };
  const handlePrevStep = () => {
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);
      setActiveItem(flatSteps[prevIndex].item.name);
    }
  };
  const handleActiveItemChange = (itemName: string) => {
    setActiveItem(itemName);
    // Sync step index to match the selected item
    const stepIdx = flatSteps.findIndex(s => s.item.name === itemName);
    if (stepIdx >= 0) {
      setCurrentStepIndex(stepIdx);
    }
  };
  const handleSelectRoom = (roomId: string) => {
    setActiveRoom(roomId);
    setShowRoomPicker(false);
  };
  const currentRoom = rooms.find(r => r.id === activeRoom) || rooms[0];
  const CurrentIcon = currentRoom.icon;
  const roomsForCurrentFloor = rooms.filter(r => r.floor === activeFloor);
  return <div className="relative w-full h-full bg-black font-sans overflow-hidden" data-id="element-1940">
      {/* 3D Viewport */}
      <div className="absolute inset-0" data-id="element-1941">
        <img src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1920&q=80" alt="Interior View" className="w-full h-full object-cover" data-id="element-1942" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30" data-id="element-1943" />
      </div>

      {/* Top Bar — Room Switcher */}
      <div className="absolute top-0 left-0 right-0 z-40 pt-2 md:pt-14 px-4 flex justify-center" data-id="element-1944">
        <button onClick={() => setShowRoomPicker(!showRoomPicker)} className="bg-black/40 backdrop-blur-xl rounded-full px-4 py-2 flex items-center gap-2 border border-white/10 active:scale-95 transition-transform" data-id="element-1945">
          <CurrentIcon className="w-4 h-4 text-white/70" data-id="element-1946" />
          <span className="text-white font-semibold text-sm" data-id="element-1947">
            {currentRoom.name}
          </span>
          <span className="text-white/30 text-xs" data-id="element-1948">·</span>
          <span className="text-white/50 text-xs" data-id="element-1949">{currentRoom.floor}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-white/50 transition-transform ${showRoomPicker ? 'rotate-180' : ''}`} data-id="element-1950" />
        </button>
      </div>

      {/* Room Picker Dropdown — List + Plan tabs */}
      <AnimatePresence data-id="element-1951">
        {showRoomPicker && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 z-40 bg-black/30" onClick={() => setShowRoomPicker(false)} data-id="element-1952" />
            {/* Dropdown Panel */}
            <motion.div initial={{
          opacity: 0,
          y: -10,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -10,
          scale: 0.95
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="absolute top-12 left-4 right-4 z-50 bg-slate-900/95 backdrop-blur-2xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl" data-id="element-1953">
              {/* Tab Bar */}
              <div className="flex border-b border-white/10" data-id="element-1954">
                <button onClick={() => setRoomPickerTab('list')} className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-semibold uppercase tracking-wider transition-colors ${roomPickerTab === 'list' ? 'text-white border-b-2 border-blue-400' : 'text-white/40'}`} data-id="element-1955">
                  <List className="w-3.5 h-3.5" data-id="element-1956" />
                  Rooms
                </button>
                <button onClick={() => setRoomPickerTab('plan')} className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-semibold uppercase tracking-wider transition-colors ${roomPickerTab === 'plan' ? 'text-white border-b-2 border-blue-400' : 'text-white/40'}`} data-id="element-1957">
                  <Map className="w-3.5 h-3.5" data-id="element-1958" />
                  Plan
                </button>
              </div>

              {/* Tab Content */}
              <AnimatePresence mode="wait" data-id="element-1959">
                {roomPickerTab === 'list' ? <motion.div key="list-tab" initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} transition={{
              duration: 0.15
            }} className="p-2 max-h-[400px] overflow-y-auto custom-scrollbar" data-id="element-1960">
                    {rooms.map(room => {
                const Icon = room.icon;
                const isActive = activeRoom === room.id;
                return <button key={room.id} onClick={() => handleSelectRoom(room.id)} className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${isActive ? 'bg-white/10' : 'active:bg-white/5'}`} data-id="element-1961">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isActive ? 'bg-white text-black' : 'bg-white/10 text-white/60'}`} data-id="element-1962">
                            <Icon className="w-4 h-4" data-id="element-1963" />
                          </div>
                          <div className="flex-1 text-left" data-id="element-1964">
                            <span className={`text-sm font-medium block ${isActive ? 'text-white' : 'text-white/80'}`} data-id="element-1965">
                              {room.name}
                            </span>
                            <span className="text-xs text-white/40" data-id="element-1966">
                              {room.floor}
                            </span>
                          </div>
                          {isActive && <Check className="w-4 h-4 text-blue-400" data-id="element-1967" />}
                        </button>;
              })}
                  </motion.div> : <motion.div key="plan-tab" initial={{
              opacity: 0,
              x: 10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: 10
            }} transition={{
              duration: 0.15
            }} className="flex flex-col" data-id="element-1968">
                    {/* Floor Selector */}
                    <div className="flex justify-center px-3 pt-3 pb-2" data-id="element-1969">
                      <div className="flex bg-white/5 rounded-full p-0.5 border border-white/10" data-id="element-1970">
                        {(['Basement', 'Main Floor', 'Upper Floor'] as const).map(floor => <button key={floor} onClick={() => setActiveFloor(floor)} className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all ${activeFloor === floor ? 'bg-white text-black shadow-sm' : 'text-white/50 active:text-white'}`} data-id="element-1971">
                            {floor}
                          </button>)}
                      </div>
                    </div>

                    {/* Floor Plan with Hotspots */}
                    <div className="relative mx-3 mb-3 rounded-xl overflow-hidden bg-slate-800 aspect-[3/4]" data-id="element-1972">
                      <motion.img key={activeFloor} src={floorPlanImages[activeFloor]} alt={`${activeFloor} Plan`} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  duration: 0.2
                }} className="w-full h-full object-contain" data-id="element-1973" />

                      {/* Room Hotspot Dots */}
                      {roomsForCurrentFloor.map(room => {
                  const Icon = room.icon;
                  const isActive = activeRoom === room.id;
                  return <button key={room.id} onClick={() => handleSelectRoom(room.id)} className="absolute group" style={{
                    top: room.planPosition.top,
                    left: room.planPosition.left,
                    transform: 'translate(-50%, -50%)'
                  }} data-id="element-1974">
                            <span className="relative flex h-8 w-8" data-id="element-1975">
                              {isActive && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" data-id="element-1976" />}
                              <span className={`relative inline-flex rounded-full h-8 w-8 border-2 items-center justify-center shadow-lg transition-colors ${isActive ? 'bg-blue-500 border-white' : 'bg-white/90 border-white/50 group-active:bg-blue-400'}`} data-id="element-1977">
                                <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-slate-700'}`} data-id="element-1978" />
                              </span>
                            </span>
                            {/* Room label on hover/active */}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 whitespace-nowrap" data-id="element-1979">
                              <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-blue-500 text-white' : 'bg-black/60 text-white/80'}`} data-id="element-1980">
                                {room.name}
                              </span>
                            </div>
                          </button>;
                })}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Floating AI Assistant Button */}
      <motion.button whileTap={{
      scale: 0.92
    }} onClick={() => setIsAssistantActive(!isAssistantActive)} animate={{
      bottom: sheetState === 'collapsed' ? 64 : sheetState === 'peek' ? 230 : 20
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className={`absolute right-4 z-30 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl ${isAssistantActive ? 'bg-blue-500 text-white' : 'bg-white text-black'}`} data-id="element-1981">
        {isAssistantActive ? <Mic className="w-6 h-6 animate-pulse" data-id="element-1982" /> : <Sparkles className="w-6 h-6" data-id="element-1983" />}
        {isAssistantActive && <span className="absolute inset-0 rounded-full border-2 border-blue-400 animate-ping opacity-75" data-id="element-1984" />}
      </motion.button>

      {/* Bottom Sheet */}
      <MobileOptionsSheet activeItem={activeItem} sheetState={sheetState} onSheetStateChange={setSheetState} onActiveItemChange={handleActiveItemChange} isStepMode currentStepIndex={currentStepIndex} totalSteps={flatSteps.length} currentStepCategory={currentStep?.category} currentStepItemName={currentStep?.item.name} onNextStep={handleNextStep} onPrevStep={handlePrevStep} data-id="element-1985" />
    </div>;
}